import { useState } from 'react'
import { motion } from 'framer-motion'
import { Icon } from '../Brand'
import { postEvaluate, getScenarios } from '../../lib/api'
import { setEvalPassRate } from '../../lib/evalStore'

// The Evaluate tab: batch-runs the golden scenarios through the real agents and
// grades them with the same fail-closed judge Chat uses, then renders a
// pass-rate scorecard. /api/evaluate makes real model calls (20–60s), so the
// button is disabled and a spinner shows while it runs.
export default function EvaluatePanel() {
  const [status, setStatus] = useState('idle') // 'idle' | 'running' | 'done' | 'error'
  const [result, setResult] = useState(null)
  const [count, setCount] = useState(0)
  const [error, setError] = useState('')

  async function run() {
    setStatus('running')
    setError('')
    setResult(null)
    // Fetch the scenario count first so the loading copy is specific.
    try {
      const s = await getScenarios()
      setCount((s?.scenarios || []).length)
    } catch { /* non-fatal — loading copy just stays generic */ }
    try {
      const data = await postEvaluate()
      setResult(data)
      // Surface the pass-rate to the Dashboard KPI (last-run value).
      if (data && data.total) setEvalPassRate(data.pass_rate)
      setStatus('done')
    } catch {
      setError('Could not reach the backend. Is the bridge running on :8080?')
      setStatus('error')
    }
  }

  const pct = result && result.total ? Math.round(result.pass_rate * 100) : 0
  const running = status === 'running'

  return (
    <>
      <motion.div
        className="page-head"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <div>
          <div className="eyebrow">Evaluate</div>
          <h1>Evaluation</h1>
          <p className="sub">Grade every golden scenario through the live agents and the fail-closed judge.</p>
        </div>
        <button className="chat-send eval-run" onClick={run} disabled={running}>
          {running ? (
            <><span className="eval-spin" /> Grading…</>
          ) : (
            <><Icon name="fact_check" /> Run evaluation</>
          )}
        </button>
      </motion.div>

      {status === 'idle' && (
        <div className="panel eval-empty">
          <div className="cs-ic"><Icon name="fact_check" /></div>
          <h3>No run yet</h3>
          <p className="ph-sub" style={{ marginBottom: 0 }}>
            Run the evaluation to score the golden scenarios. Real model calls — this can take 20–60 seconds.
          </p>
        </div>
      )}

      {running && (
        <div className="panel eval-empty">
          <span className="eval-spin lg" />
          <h3 style={{ marginTop: 14 }}>Grading {count || ''} scenarios…</h3>
          <p className="ph-sub" style={{ marginBottom: 0 }}>
            Each scenario runs through the real orchestrator and the judge — hang tight.
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="chat-notice"><Icon name="error" />{error}</div>
      )}

      {status === 'done' && result && (
        <>
          <div className="section-label">Scorecard</div>
          <div className="eval-score">
            <div className="eval-rate">
              <div className={`eval-pct ${pct >= 80 ? 'good' : pct >= 50 ? 'mid' : 'low'}`}>{pct}%</div>
              <div className="eval-rate-lab">pass rate</div>
            </div>
            <div className="eval-counts">
              <div className="eval-count">
                <div className="eval-count-n" style={{ color: 'var(--teal-500)' }}>{result.passed}</div>
                <div className="lab">passed</div>
              </div>
              <div className="eval-count">
                <div className="eval-count-n" style={{ color: 'var(--peach-fg)' }}>{result.total - result.passed}</div>
                <div className="lab">failed</div>
              </div>
              <div className="eval-count">
                <div className="eval-count-n">{result.total}</div>
                <div className="lab">total</div>
              </div>
            </div>
          </div>

          <div className="section-label">Cases ({result.cases.length})</div>
          <div className="panel eval-cases">
            {result.cases.map((c) => (
              <div className={`eval-case ${c.passed ? 'pass' : 'fail'}`} key={c.id}>
                <div className="eval-case-ic">
                  <Icon name={c.passed ? 'check_circle' : 'cancel'} />
                </div>
                <div className="eval-case-body">
                  <div className="eval-case-q">{c.question}</div>
                  <div className="eval-case-reason ph-sub" style={{ marginBottom: 0 }}>{c.reason || '—'}</div>
                </div>
                <div className="eval-case-agent ctl-cap">{c.agent || 'out of scope'}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}
